import { ref, push, get, set, update, serverTimestamp, remove } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { ChatAuditService } from './ChatAuditService';

export const GroupService = {
    /**
     * Create a new group chat — ATOMIC single update() call.
     * Creator becomes owner + admin + member automatically.
     * @param {Object} params
     * @param {string} params.name - Group name (required)
     * @param {string} params.description - Group description (optional)
     * @param {string} params.photo - Group photo URL (optional)
     * @param {string[]} params.memberIds - Array of user IDs to add
     * @param {Object} params.creator - { uid, name, email }
     * @returns {Promise<{success: boolean, chatId?: string, error?: string}>}
     */
    createGroup: async ({ name, description, photo, memberIds, creator }) => {
        if (!name?.trim() || !creator?.uid) {
            return { success: false, error: 'Group name and creator are required.' };
        }
        if (!memberIds || memberIds.length === 0) {
            return { success: false, error: 'At least one member is required.' };
        }

        try {
            // Generate safe Firebase push ID (not based on member list)
            const groupRef = push(ref(db, 'conversations'));
            const groupId = groupRef.key;

            // Build members map (creator + selected members)
            const allMemberIds = [creator.uid, ...memberIds.filter(id => id !== creator.uid)];
            const membersMap = {};
            allMemberIds.forEach(uid => { membersMap[uid] = true; });

            // Generate a push key for the system message
            const sysMsgKey = push(ref(db, 'conversations/__temp')).key;

            // Step 1: Create the conversation with set() — avoids child .write rule conflicts
            const groupData = {
                id: groupId,
                type: 'group',
                name: name.trim(),
                description: description?.trim() || '',
                photo: photo || '',
                ownerId: creator.uid,
                createdBy: creator.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                admins: { [creator.uid]: true },
                members: membersMap,
                lastMessage: {
                    text: `${creator.name || creator.email} created group "${name.trim()}"`,
                    senderId: 'system',
                    timestamp: Date.now(),
                    type: 'system'
                },
                messages: {
                    [sysMsgKey]: {
                        text: `${creator.name || creator.email} created this group`,
                        senderId: 'system',
                        type: 'system',
                        timestamp: Date.now()
                    }
                }
            };

            await set(groupRef, groupData);

            // Step 2: Add group to each member's chat list
            const chatListUpdates = {};
            allMemberIds.forEach(uid => {
                chatListUpdates[`users/${uid}/chats/${groupId}`] = groupId;
            });
            await update(ref(db), chatListUpdates);

            // Audit (non-blocking)
            ChatAuditService.logGroupCreated(groupId, {
                actorId: creator.uid,
                actorName: creator.name || creator.email,
                groupName: name.trim(),
                memberCount: allMemberIds.length
            });

            return { success: true, chatId: groupId };
        } catch (error) {
            console.error('[GroupService] createGroup failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Get the role of a user in a group.
     * @returns {'owner' | 'admin' | 'member' | null}
     */
    getGroupRole: async (chatId, userId) => {
        try {
            const convSnap = await get(ref(db, `conversations/${chatId}`));
            if (!convSnap.exists()) return null;
            const data = convSnap.val();

            if (data.type !== 'group') return null;
            if (data.ownerId === userId) return 'owner';
            if (data.admins?.[userId]) return 'admin';
            if (data.members?.[userId]) return 'member';
            return null;
        } catch (error) {
            console.error('[GroupService] getGroupRole failed:', error);
            return null;
        }
    },

    /**
     * Check if user can pin/unpin messages.
     * 1:1 chat: always true.
     * Group chat: only owner/admin.
     */
    canPin: async (chatId, userId) => {
        try {
            const convSnap = await get(ref(db, `conversations/${chatId}`));
            if (!convSnap.exists()) return false;
            const data = convSnap.val();

            // 1:1 chat — both users can pin
            if (data.type !== 'group') return true;

            // Group — only owner or admin
            if (data.ownerId === userId) return true;
            if (data.admins?.[userId]) return true;
            return false;
        } catch (error) {
            console.error('[GroupService] canPin failed:', error);
            return false;
        }
    },

    /**
     * Update group info (name, description, photo).
     * Owner or admin can modify.
     */
    updateGroupInfo: async (chatId, infoUpdates, userId) => {
        try {
            const role = await GroupService.getGroupRole(chatId, userId);
            if (role !== 'owner' && role !== 'admin') {
                return { success: false, error: 'Only group owner or admin can update group info.' };
            }

            const allowedFields = ['name', 'description', 'photo'];
            const safeUpdates = {};
            Object.keys(infoUpdates).forEach(key => {
                if (allowedFields.includes(key)) {
                    safeUpdates[`conversations/${chatId}/${key}`] = infoUpdates[key];
                }
            });
            safeUpdates[`conversations/${chatId}/updatedAt`] = serverTimestamp();

            await update(ref(db), safeUpdates);

            ChatAuditService.logGroupInfoChanged(chatId, {
                actorId: userId,
                actorName: '',
                field: Object.keys(infoUpdates).join(', '),
                before: 'previous',
                after: Object.values(infoUpdates).join(', ')
            });

            return { success: true };
        } catch (error) {
            console.error('[GroupService] updateGroupInfo failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Add members to group. Owner/admin only.
     */
    addMembers: async (chatId, memberIds, addedByUserId) => {
        try {
            const role = await GroupService.getGroupRole(chatId, addedByUserId);
            if (role !== 'owner' && role !== 'admin') {
                return { success: false, error: 'Only owner/admin can add members.' };
            }

            const updates = {};
            memberIds.forEach(uid => {
                updates[`conversations/${chatId}/members/${uid}`] = true;
                updates[`users/${uid}/chats/${chatId}`] = chatId;
            });
            updates[`conversations/${chatId}/updatedAt`] = serverTimestamp();

            await update(ref(db), updates);

            memberIds.forEach(uid => {
                ChatAuditService.logMemberAdded(chatId, {
                    actorId: addedByUserId,
                    actorName: '',
                    memberId: uid,
                    memberName: ''
                });
            });

            return { success: true };
        } catch (error) {
            console.error('[GroupService] addMembers failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Remove a member from group. Owner/admin only. Owner cannot be removed.
     * Cleans up both conversations/members and users/chats.
     */
    removeMember: async (chatId, memberId, removedByUserId) => {
        try {
            const convSnap = await get(ref(db, `conversations/${chatId}`));
            if (!convSnap.exists()) return { success: false, error: 'Group not found.' };
            const data = convSnap.val();

            // Cannot remove owner
            if (data.ownerId === memberId) {
                return { success: false, error: 'Cannot remove the group owner. Transfer ownership first.' };
            }

            const role = await GroupService.getGroupRole(chatId, removedByUserId);
            if (role !== 'owner' && role !== 'admin') {
                return { success: false, error: 'Only owner/admin can remove members.' };
            }

            const updates = {};
            updates[`conversations/${chatId}/members/${memberId}`] = null;
            updates[`conversations/${chatId}/admins/${memberId}`] = null; // Also remove admin if they were one
            updates[`users/${memberId}/chats/${chatId}`] = null;
            updates[`conversations/${chatId}/updatedAt`] = serverTimestamp();

            await update(ref(db), updates);

            ChatAuditService.logMemberRemoved(chatId, {
                actorId: removedByUserId,
                actorName: '',
                memberId,
                memberName: ''
            });

            return { success: true };
        } catch (error) {
            console.error('[GroupService] removeMember failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Promote member to admin. Owner only.
     */
    promoteToAdmin: async (chatId, memberId, promotedByUserId) => {
        try {
            const role = await GroupService.getGroupRole(chatId, promotedByUserId);
            if (role !== 'owner') {
                return { success: false, error: 'Only the owner can promote admins.' };
            }

            // Must be a member
            const memberRole = await GroupService.getGroupRole(chatId, memberId);
            if (!memberRole) {
                return { success: false, error: 'User is not a group member.' };
            }

            await update(ref(db), {
                [`conversations/${chatId}/admins/${memberId}`]: true,
                [`conversations/${chatId}/updatedAt`]: serverTimestamp()
            });

            ChatAuditService.logAdminPromoted(chatId, {
                actorId: promotedByUserId,
                actorName: '',
                memberId
            });

            return { success: true };
        } catch (error) {
            console.error('[GroupService] promoteToAdmin failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Demote admin back to member. Owner only.
     */
    demoteAdmin: async (chatId, memberId, demotedByUserId) => {
        try {
            const role = await GroupService.getGroupRole(chatId, demotedByUserId);
            if (role !== 'owner') {
                return { success: false, error: 'Only the owner can demote admins.' };
            }

            await update(ref(db), {
                [`conversations/${chatId}/admins/${memberId}`]: null,
                [`conversations/${chatId}/updatedAt`]: serverTimestamp()
            });

            ChatAuditService.logAdminDemoted(chatId, {
                actorId: demotedByUserId,
                actorName: '',
                memberId
            });

            return { success: true };
        } catch (error) {
            console.error('[GroupService] demoteAdmin failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Transfer ownership. Only current owner can transfer.
     * New owner must already be a group member.
     */
    transferOwnership: async (chatId, newOwnerId, currentOwnerId) => {
        try {
            const convSnap = await get(ref(db, `conversations/${chatId}`));
            if (!convSnap.exists()) return { success: false, error: 'Group not found.' };
            const data = convSnap.val();

            if (data.ownerId !== currentOwnerId) {
                return { success: false, error: 'Only the current owner can transfer ownership.' };
            }

            if (!data.members?.[newOwnerId]) {
                return { success: false, error: 'New owner must be a group member.' };
            }

            await update(ref(db), {
                [`conversations/${chatId}/ownerId`]: newOwnerId,
                [`conversations/${chatId}/admins/${newOwnerId}`]: true, // Ensure new owner is also admin
                [`conversations/${chatId}/updatedAt`]: serverTimestamp()
            });

            ChatAuditService.logOwnershipTransferred(chatId, {
                actorId: currentOwnerId,
                actorName: '',
                newOwnerId,
                newOwnerName: ''
            });

            return { success: true };
        } catch (error) {
            console.error('[GroupService] transferOwnership failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Leave group. Owner cannot leave without transferring ownership first.
     */
    leaveGroup: async (chatId, userId) => {
        try {
            const convSnap = await get(ref(db, `conversations/${chatId}`));
            if (!convSnap.exists()) return { success: false, error: 'Group not found.' };
            const data = convSnap.val();

            if (data.ownerId === userId) {
                return { success: false, error: 'Owner cannot leave. Transfer ownership first.' };
            }

            const updates = {};
            updates[`conversations/${chatId}/members/${userId}`] = null;
            updates[`conversations/${chatId}/admins/${userId}`] = null;
            updates[`users/${userId}/chats/${chatId}`] = null;
            updates[`conversations/${chatId}/updatedAt`] = serverTimestamp();

            await update(ref(db), updates);
            return { success: true };
        } catch (error) {
            console.error('[GroupService] leaveGroup failed:', error);
            return { success: false, error: error.message };
        }
    }
};
